import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useFormContext, useFieldArray, Controller } from "react-hook-form";
import {
  Grid,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Autocomplete,
  Alert,
  useMediaQuery
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useTheme } from "@mui/material/styles";
import TooltipComponent from "../../common/Tooltip";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 100 }, (_, i) => currentYear - i);

// Helper: Check if an experience entry is fully empty (no user input)
const isExperienceEmpty = (item) => {
  if (!item) return true;
  const role = (item.role || "").trim();
  const company = (item.company || "").trim();
  const location = (item.location || "").trim();
  const startDate = item.startDate;
  const endDate = item.endDate;
  const current = !!item.current;
  return !role && !company && !location && !startDate && (!endDate || current);
};

// Helper: Check if an experience entry is fully complete (all required fields filled)
const isExperienceComplete = (item) => {
  if (!item) return false;
  const role = (item.role || "").trim();
  const company = (item.company || "").trim();
  const location = (item.location || "").trim();
  const startDate = item.startDate;
  const current = !!item.current;
  const endDate = item.endDate;
  return !!(role && company && location && startDate && (current || endDate));
};

function WorkExperienceQuestionComponent({ question }) {
  const {
    control,
    formState: { errors },
    getValues,
    setValue,
    watch,
  } = useFormContext();
  const theme = useTheme();
  const IncognitoModeStatus = useSelector(
    (state) => state.IncognitoMode?.IncognitoUser || false
  );
  const isMobile = useMediaQuery("(max-width:600px)");

  const { fields, append, remove } = useFieldArray({
    control,
    name: question.questionName,
  });

  useEffect(() => {
    const currentVal = getValues(question.questionName);

    // 1. Initialize empty form
    if (fields.length === 0) {
      if (!currentVal || currentVal.length === 0) {
        append({
          company: "",
          role: "",
          location: "",
          startDate: "",
          endDate: "",
          current: false,
          duration: "",
          workInMonths: 0
        });
        return;
      }
    }

    // 2. Normalize existing data: Set 'current' to true if duration contains 'present'
    if (currentVal && currentVal.length > 0) {
      let shouldUpdate = false;
      const normalizedValues = currentVal.map((item) => {
        const hasPresent = item.duration && typeof item.duration === 'string' && item.duration.toLowerCase().includes("present");
        
        // Trust 'Present' in duration logic over endDate existence
        if (!item.current && hasPresent) {
          shouldUpdate = true;
          return { ...item, current: true };
        }
        return item;
      });

      if (shouldUpdate) {
        setValue(question.questionName, normalizedValues);
      }
    }
  }, [fields, append, getValues, setValue, question.questionName]);

    const updateDuration = (index, currentData) => {
    const row = getValues(`${question.questionName}.${index}`);
    const data = { ...row, ...currentData };

    if (!data.startDate) {
        setValue(`${question.questionName}.${index}.duration`, "");
        setValue(`${question.questionName}.${index}.workInMonths`, 0);
        return;
    }

    let start = new Date(data.startDate);
    let end = data.current ? new Date() : (data.endDate ? new Date(data.endDate) : null);

    if (!isValidDate(start)) {
         setValue(`${question.questionName}.${index}.duration`, "");
         return;
    }
    
    let durationStr = "";
    let monthsDiff = 0;

    const startMonthStr = (start.getUTCMonth() + 1).toString().padStart(2, '0');
    const startYearStr = start.getUTCFullYear();
    const formattedStart = `${startMonthStr}/${startYearStr}`;

    if (data.current) {
        durationStr = `${formattedStart} - Present`;
        end = new Date(); 
        monthsDiff = (end.getFullYear() - start.getUTCFullYear()) * 12 + (end.getMonth() - start.getUTCMonth()) + 1; 
    } else if (isValidDate(end)) {
        const endMonthStr = (end.getUTCMonth() + 1).toString().padStart(2, '0');
        const endYearStr = end.getUTCFullYear();
        const formattedEnd = `${endMonthStr}/${endYearStr}`;
        durationStr = `${formattedStart} - ${formattedEnd}`;
        
        monthsDiff = (end.getUTCFullYear() - start.getUTCFullYear()) * 12 + (end.getUTCMonth() - start.getUTCMonth()); 
    }

    setValue(`${question.questionName}.${index}.duration`, durationStr);
    setValue(`${question.questionName}.${index}.workInMonths`, monthsDiff > 0 ? monthsDiff : 0);
  };

  const isValidDate = (d) => {
    return d instanceof Date && !isNaN(d);
  };

  // Validation: each experience must be either fully empty OR fully complete
  const partialValidationError = errors?.[`${question.questionName}__completeOrEmpty`]?.message;
  const experiences = watch(question.questionName) || [];
  const invalidExperienceIndices = partialValidationError
    ? (() => {
        const exps = experiences;
        const hasComplete = exps.some(isExperienceComplete);
        if (question.rules.required && !hasComplete) {
          return exps.map((item, i) => (isExperienceEmpty(item) ? i : -1)).filter((i) => i >= 0);
        }
        return exps
          .map((item, i) => (!isExperienceEmpty(item) && !isExperienceComplete(item) ? i : -1))
          .filter((i) => i >= 0);
      })()
    : [];

  return (
    <Grid
      item
      xs={12}
      sx={{
        mb: 2,
        ml: question?.isSuggested ? 2 : 0,
        backgroundColor: question?.isSuggested
          ? theme.palette.primary.nestedQuestionBackground
          : "",
        py: question?.isSuggested ? 2 : 0,
        px: question?.isSuggested ? 1.5 : 0,
        borderRadius: question?.isSuggested ? 2 : 0,
        border: question?.isSuggested ? "1px solid #fff" : "none",
        boxShadow: question?.isSuggested
          ? "0px 2px 4px rgba(0, 0, 0, 0.2)"
          : 0,
        cursor: question?.isSuggested ? "pointer" : "",
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography className="stepper-question">
          {question.label}
          {question.rules.required && (
            <>
              &nbsp;<span style={{ color: "red" }}>*</span>
            </>
          )}
        </Typography>
        {question.question_info && (
           <TooltipComponent message={question?.question_info} />
        )}
      </Box>

      {/* Custom validation: each experience must be either fully empty OR fully complete */}
      <Controller
        name={`${question.questionName}__completeOrEmpty`}
        control={control}
        defaultValue={null}
        rules={{
          validate: () => {
            const exps = getValues(question.questionName) || [];
            const hasComplete = exps.some(isExperienceComplete);
            if (question.rules.required && !hasComplete) {
              return "At least one work experience is required";
            }
            for (let i = 0; i < exps.length; i++) {
              const item = exps[i];
              if (!isExperienceEmpty(item) && !isExperienceComplete(item)) {
                return `Experience ${i + 1}: Please complete all fields or remove this experience`;
              }
            }
            return true;
          },
        }}
        render={() => null}
      />

      {partialValidationError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {partialValidationError}
        </Alert>
      )}

      {fields.map((item, index) => {
          const isCurrent = watch(`${question.questionName}.${index}.current`);
          const isHighlighted = invalidExperienceIndices.includes(index);
          
          return (
        <Card
          key={item.id}
          variant="outlined"
          sx={{
            mb: 2,
            position: 'relative',
            overflow: 'visible',
            ...(isHighlighted && {
              border: '2px solid',
              borderColor: theme.palette.warning?.main || '#ed6c02',
            }),
          }}
        >
          <CardContent
            sx={{ backgroundColor: IncognitoModeStatus ? theme.palette.primary.incognitoBackground : "inherit" }}
          >
             {fields.length > 1 && (
               <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <IconButton
                    aria-label="delete"
                    onClick={() => remove(index)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
               </Box>
            )}
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name={`${question.questionName}.${index}.role`}
                  control={control}
                  rules={{ required: question.rules.required ? "Role is required" : false }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Job Title / Role"
                                              size={isMobile?'small':'medium'}

                      variant="outlined"
                      fullWidth
                      error={!!errors?.[question.questionName]?.[index]?.role}
                      helperText={errors?.[question.questionName]?.[index]?.role?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                 <Controller
                  name={`${question.questionName}.${index}.company`}
                  control={control}
                  rules={{ required: question.rules.required ? "Company is required" : false }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Company Name"
                                              size={isMobile?'small':'medium'}

                      variant="outlined"
                      fullWidth
                      error={!!errors?.[question.questionName]?.[index]?.company}
                      helperText={errors?.[question.questionName]?.[index]?.company?.message}
                    />
                  )}
                />
              </Grid>

               <Grid item xs={12}>
                 <Controller
                  name={`${question.questionName}.${index}.location`}
                  control={control}
                  rules={{ required: question.rules.required ? "Location is required" : false }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Location"
                                              size={isMobile?'small':'medium'}

                      variant="outlined"
                      fullWidth
                      error={!!errors?.[question.questionName]?.[index]?.location}
                      helperText={errors?.[question.questionName]?.[index]?.location?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                  <Controller
                    name={`${question.questionName}.${index}.current`}
                    control={control}
                    render={({ field }) => (
                        <FormControlLabel
                        sx={{'& .MuiFormControlLabel-label':{fontSize:{xs:'12px',md:'14px'}}}}
                            control={
                                <Checkbox 
                                    {...field} 
                                                            size={isMobile?'small':'medium'}

                                    checked={field.value} 
                                    onChange={(e) => {
                                        field.onChange(e.target.checked);
                                        updateDuration(index, { current: e.target.checked });
                                    }}
                                />
                            }
                            label="I am currently working in this role"
                        />
                    )}
                  />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" gutterBottom>Start date</Typography>
                <Grid container spacing={2}>
                    <Controller
                        name={`${question.questionName}.${index}.startDate`}
                        control={control}
                        rules={{ required: question.rules.required ? "Start Date is required" : false }}
                        render={({ field }) => (
                          <SingleDateSelector
                             value={field.value}
                             onChange={(newDate) => {
                               field.onChange(newDate);
                               updateDuration(index, { startDate: newDate });
                             }}
                             error={errors?.[question.questionName]?.[index]?.startDate}
                          />
                        )}
                    />
                </Grid>
                {errors?.[question.questionName]?.[index]?.startDate && (
                    <FormHelperText error>{errors?.[question.questionName]?.[index]?.startDate?.message}</FormHelperText>
                )}
              </Grid>

              <Grid item xs={12} sm={6} sx={{ visibility: isCurrent ? 'hidden' : 'visible' }}>
                <Typography variant="body2" gutterBottom>End date</Typography>
                <Grid container spacing={2}>
                <Controller
                        name={`${question.questionName}.${index}.endDate`}
                        control={control}
                        rules={{ 
                            validate: (value) => {
                                if (!question.rules.required) return true;
                                if (isCurrent) return true;
                                return value ? true : "End Date is required";
                            }
                         }}
                        render={({ field }) => (
                             <SingleDateSelector
                                value={field.value}
                                onChange={(newDate) => {
                                  field.onChange(newDate);
                                  updateDuration(index, { endDate: newDate });
                                }}
                                error={errors?.[question.questionName]?.[index]?.endDate}
                                disabled={isCurrent}
                             />
                        )}
                    />
                </Grid>
                  {errors?.[question.questionName]?.[index]?.endDate && !isCurrent && (
                   <FormHelperText error>{errors?.[question.questionName]?.[index]?.endDate?.message}</FormHelperText>
                )}
              </Grid>

            </Grid>
            {isHighlighted && (
              <FormHelperText error sx={{ mt: 1 }}>
                Please complete all fields or remove this experience
              </FormHelperText>
            )}
          </CardContent>
        </Card>
      );
    })}

      <Box display="flex" justifyContent="center" mt={2}>
        <Button
            startIcon={<AddIcon />}
            variant="outlined"
            onClick={() => append({ company: "", role: "", location: "", startDate: "", endDate: "", current: false, duration: "", workInMonths: 0 })}
            sx={{ 
                fontWeight: 'bold'
            }}
        >
            Add Another Experience
        </Button>
      </Box>
    </Grid>
  );
}

const SingleDateSelector = ({ value, onChange, error, disabled = false }) => {
  // Internal state to hold values even if they don't form a valid date yet
  const [selectedMonth, setSelectedMonth] = React.useState(null);
  const [selectedYear, setSelectedYear] = React.useState(null);
  const isMobile = useMediaQuery("(max-width:600px)");

  const currentDate = new Date();
  const currentYearVal = currentDate.getFullYear();
  const currentMonthIndex = currentDate.getMonth();

  // Helper to parse existing value into internal state
  // Only runs when value changes effectively (and is not null/empty when we expect it)
  useEffect(() => {
    if (value) {
      const d = new Date(value);
      if (!isNaN(d.getTime())) {
          setSelectedMonth(MONTHS[d.getUTCMonth()]);
          setSelectedYear(d.getUTCFullYear());
      }
    } else {
        // If value is cleared externally, we might want to clear internal state
        // BE CAREFUL: If we clear internal state here, we might cycle 
        // if onChange(null) triggered this. 
        // Logic: If value is empty, clear visual state too.
        setSelectedMonth(null);
        setSelectedYear(null);
    }
  }, [value]);

  const handleDateChange = (newMonth, newYear) => {
     // 1. Update visual state immediately
     setSelectedMonth(newMonth); 
     setSelectedYear(newYear);

     // 2. Check if we have enough info to commit a date
     if (newMonth && newYear) {
         const monthIndex = MONTHS.indexOf(newMonth);
         // Construct Date in UTC
         const d = new Date(Date.UTC(newYear, monthIndex, 1));
         onChange(d.toISOString());
     } else {
         // If partial date, we assume "Invalid Date" for the form -> null
         onChange(""); 
     }
  };

  // Filter months if current year is selected
  const availableMonths = React.useMemo(() => {
      // If selected year is the current year, filter months to exclude future months
      if (selectedYear === currentYearVal) {
          return MONTHS.slice(0, currentMonthIndex + 1);
      }
      return MONTHS;
  }, [selectedYear, currentYearVal, currentMonthIndex]);

  return (
    <>
      <Grid item xs={6}>
        <Autocomplete
                                size={isMobile ?'small':'medium'}

          fullWidth
          options={availableMonths}
          value={selectedMonth}
          disabled={disabled}
          onChange={(event, newValue) => {
             handleDateChange(newValue, selectedYear);
          }}
          renderInput={(params) => <TextField {...params} label="Month" error={!!error} />}
        />
      </Grid>
      <Grid item xs={6}>
        <Autocomplete
          fullWidth
                                  size={isMobile?'small':'medium'}

          options={YEARS}
          value={selectedYear}
          getOptionLabel={(option) => String(option)}
          isOptionEqualToValue={(option, value) => option === value}
          disabled={disabled}
          onChange={(event, newValue) => {
              // If switching to current year and selected month is future, clear month
              if (newValue === currentYearVal && selectedMonth) {
                  const monthIdx = MONTHS.indexOf(selectedMonth);
                  if (monthIdx > currentMonthIndex) {
                      handleDateChange(null, newValue);
                      return;
                  }
              }
              handleDateChange(selectedMonth, newValue);
          }}
          renderInput={(params) => <TextField {...params} label="Year" error={!!error} />}
        />
      </Grid>
    </>
  );
};

export default WorkExperienceQuestionComponent;
